import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ViewOptionsAction } from 'modules/shift_management/hooks/reducers/viewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useViewOptions } from 'modules/shift_management/hooks/useViewOptions'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import ViewOptions, {
  CoverageOptions,
  EmployeeOptions,
  OtherOptions,
  ScheduleOptions,
} from 'modules/shift_management/store/internal/models/ViewOptions'

import CoverageOptionsContent from './CoverageOptionsContent'
import EmployeeOptionsContent from './EmployeeOptionsContent'
import OtherOptionsContent from './OtherOptionsContent'
import ScheduleOptionsContent from './ScheduleOptionsContent'

interface Props {
  hasCoverage: boolean
}

const Content = ({ hasCoverage }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const [{ allViewOptions }, dispatch] = useViewOptions()
  const localStorageKey = LOCAL_STORAGE_KEYS.shiftManagementAllViewOptions
  const [, setAllViewOptions] = useLocalStorage<ViewOptions[]>(localStorageKey)
  const hasBackupShifts = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_BACKUP_SHIFTS)

  const boxProps = {
    padding: 's16',
    gap: 's16',
    width: 'full',
    background: 'grey100',
    borderRadius: { all: 'abs010' },
    maxWidth: 's200',
  } as const

  const performAction = async (action: ViewOptionsAction) => {
    // Keep this await to ensure the state is updated before saving to local storage
    await dispatch(action)
    setAllViewOptions(allViewOptions.allViewOptions)
  }

  const performEmployeeAction = (option: keyof EmployeeOptions) => {
    performAction({ type: 'TOGGLE_EMPLOYEE_OPTION', view, option })
  }

  const performScheduleAction = (option: keyof ScheduleOptions) => {
    performAction({ type: 'TOGGLE_SCHEDULE_OPTION', view, option })
  }

  const performCoverageAction = (option: keyof CoverageOptions) => {
    performAction({ type: 'TOGGLE_COVERAGE_OPTION', view, option })
  }

  const performOtherAction = (option: keyof OtherOptions) => {
    performAction({ type: 'TOGGLE_OTHER_OPTION', view, option })
  }

  return (
    <Box padding="s16" flexDirection="row" gap="s8">
      <Box gap="s8" flexDirection="column" justifyContent="spaceBetween">
        <Box {...boxProps}>
          <EmployeeOptionsContent onChange={performEmployeeAction} />
        </Box>
        {hasBackupShifts && (
          <Box {...boxProps} flexGrow>
            <OtherOptionsContent onChange={performOtherAction} />
          </Box>
        )}
      </Box>
      <Box {...boxProps}>
        <ScheduleOptionsContent onChange={performScheduleAction} />
      </Box>
      {hasCoverage && (
        <Box {...boxProps}>
          <CoverageOptionsContent onChange={performCoverageAction} />
        </Box>
      )}
    </Box>
  )
}

export default observer(Content)
